// SAM-633 — pure logic for the public pay page: page state machine, backend error_code → outcome,
// countdown, business-local date/time and money formatting, request-body builders and the 3DS
// chain. No React and no axios here, so every rule is unit tested (see __tests__).

import { formatMoneyAmount } from '../../../utils/money';
import type { Lang } from '../../../i18n/publicEvents';
import type {
  PaymentLinkAuthData,
  PaymentLinkCard,
  PaymentLink3dsBody,
  PaymentLinkEnrollData,
  PaymentLinkPass,
  PaymentLinkPayBody,
  PaymentLinkSetupData,
  PaymentLinkSummary,
  PaymentLinkValidateData,
} from '../../../api/paymentLinkApi';
import type { PayLinkStringKey } from './payLinkStrings';

// ─── Page state machine ──────────────────────────────────────────────────────

export type PayLinkPhase =
  | 'loading'
  | 'loadError' // summary could not be read (network / 5xx) — offer a retry
  | 'unavailable' // 404 PAYMENT_LINK_UNAVAILABLE: one generic screen for every unusable token
  | 'expired' // the countdown reached zero on this page
  | 'locked' // attempt caps reached (429 / 402 ..._LINK_LOCKED / summary with `message`)
  | 'form' // payable: summary + card form
  | 'submitting' // 3DS + pay in flight (button disabled)
  | 'polling' // a payment may be in flight elsewhere: re-read the summary, never re-post
  | 'processing' // charged, confirmation pending: never offer to pay again
  | 'paid' // receipt
  | 'stopped'; // terminal, not payable here (superseded, business closed)

export interface PayLinkNotice {
  key: PayLinkStringKey;
  attemptsRemaining?: number;
}

export interface PayLinkState {
  phase: PayLinkPhase;
  summary?: PaymentLinkSummary;
  /** Epoch ms at which the hold ends, from the server's `seconds_remaining` at read time. */
  deadline?: number;
  notice?: PayLinkNotice;
  step?: 'verifying' | 'charging';
  stoppedKey?: PayLinkStringKey;
  /** Bumped whenever card data must be wiped (the form remounts with a new key). */
  cardResetKey: number;
}

export const initialPayLinkState: PayLinkState = { phase: 'loading', cardResetKey: 0 };

export type PaymentLinkOutcome =
  | { kind: 'unavailable' }
  | { kind: 'locked' }
  | { kind: 'processing' }
  | { kind: 'poll' }
  | { kind: 'refresh' }
  | { kind: 'loadError' }
  | { kind: 'stopped'; key: PayLinkStringKey }
  | { kind: 'retry'; key: PayLinkStringKey; attemptsRemaining?: number };

export type PayLinkAction =
  | { type: 'LOADED'; summary: PaymentLinkSummary; now: number; fromPoll?: boolean }
  | { type: 'OUTCOME'; outcome: PaymentLinkOutcome }
  | { type: 'SUBMIT' }
  | { type: 'STEP'; step: 'verifying' | 'charging' }
  | { type: 'TICK'; now: number }
  | { type: 'POLL_GAVE_UP' }
  | { type: 'RELOAD' };

/** Which screen a freshly read summary means. */
export function phaseForSummary(summary: PaymentLinkSummary): PayLinkPhase {
  if (summary.status === 'paid' && summary.receipt) return 'paid';
  if (summary.status === 'processing') return 'processing';
  if (summary.payable) return 'form';
  if (summary.status === 'pending_payment') {
    // Not payable while pending: `locked` carries a message (payment_link._public_view); otherwise
    // the link is `paying` — another tab/attempt holds it right now.
    return summary.message ? 'locked' : 'polling';
  }
  return 'unavailable'; // expired / revoked / cancelled (normally a 404 already)
}

const TERMINAL: PayLinkPhase[] = ['unavailable', 'expired', 'locked', 'processing', 'paid', 'stopped'];
export const isTerminalPhase = (phase: PayLinkPhase) => TERMINAL.includes(phase);

function withCardWipe(prev: PayLinkState, next: Omit<PayLinkState, 'cardResetKey'>): PayLinkState {
  // Any move to a terminal screen, or away from the form after an attempt, drops card data.
  const wipe = isTerminalPhase(next.phase) || (prev.phase === 'submitting' && next.phase !== 'submitting');
  return { ...next, cardResetKey: wipe ? prev.cardResetKey + 1 : prev.cardResetKey };
}

export function payLinkReducer(state: PayLinkState, action: PayLinkAction): PayLinkState {
  switch (action.type) {
    case 'RELOAD':
      return { ...state, phase: 'loading', notice: undefined, step: undefined };
    case 'LOADED': {
      const phase = phaseForSummary(action.summary);
      const seconds = Number(action.summary.seconds_remaining);
      const deadline = Number.isFinite(seconds) ? action.now + Math.max(0, seconds) * 1000 : undefined;
      // A poll that lands back on a payable link means the other attempt ended without a charge.
      const notice: PayLinkNotice | undefined =
        phase === 'form' && action.fromPoll ? { key: 'err_previousAttempt' } : undefined;
      if (phase === 'form' && deadline !== undefined && deadline <= action.now) {
        return withCardWipe(state, { phase: 'expired', summary: action.summary });
      }
      return withCardWipe(state, { phase, summary: action.summary, deadline, notice });
    }
    case 'SUBMIT':
      if (state.phase !== 'form') return state; // double-submit guard (the button is disabled too)
      return { ...state, phase: 'submitting', notice: undefined, step: 'verifying' };
    case 'STEP':
      return state.phase === 'submitting' ? { ...state, step: action.step } : state;
    case 'TICK':
      if (state.phase === 'form' && state.deadline !== undefined && action.now >= state.deadline) {
        return withCardWipe(state, { phase: 'expired', summary: state.summary });
      }
      return state;
    case 'POLL_GAVE_UP':
      return state.phase === 'polling' ? withCardWipe(state, { phase: 'processing', summary: state.summary, notice: { key: 'stillConfirmingBody' } }) : state;
    case 'OUTCOME': {
      const o = action.outcome;
      const base = { summary: state.summary, deadline: state.deadline };
      switch (o.kind) {
        case 'unavailable':
          return withCardWipe(state, { ...base, phase: 'unavailable' });
        case 'locked':
          return withCardWipe(state, { ...base, phase: 'locked' });
        case 'processing':
          return withCardWipe(state, { ...base, phase: 'processing' });
        case 'poll':
        case 'refresh':
          return withCardWipe(state, { ...base, phase: 'polling' });
        case 'loadError':
          return withCardWipe(state, { ...base, phase: 'loadError' });
        case 'stopped':
          return withCardWipe(state, { ...base, phase: 'stopped', stoppedKey: o.key });
        case 'retry':
          if (state.phase === 'loading') return withCardWipe(state, { ...base, phase: 'loadError' });
          return withCardWipe(state, {
            ...base,
            phase: 'form',
            notice: { key: o.key, attemptsRemaining: o.attemptsRemaining },
          });
      }
      return state;
    }
  }
  return state;
}

// ─── Backend error_code → outcome ────────────────────────────────────────────

const RETRY_KEYS: Record<string, PayLinkStringKey> = {
  THREEDS_REQUIRED: 'err_THREEDS_REQUIRED',
  THREEDS_MISMATCH: 'err_THREEDS_MISMATCH',
  THREEDS_FAILED: 'err_THREEDS_FAILED',
  THREEDS_TIMEOUT: 'err_THREEDS_TIMEOUT',
  INVALID_3DS_STEP: 'err_THREEDS_FAILED',
  CARD_REQUIRED: 'err_CARD_REQUIRED',
  CARDHOLDER_NAME_REQUIRED: 'err_CARDHOLDER_NAME_REQUIRED',
  PAYMENT_GATEWAY_ERROR: 'err_PAYMENT_GATEWAY_ERROR',
  PAYMENT_TEMPORARILY_UNAVAILABLE: 'err_PAYMENT_TEMPORARILY_UNAVAILABLE',
};

export type PaymentLinkCallKind = 'summary' | '3ds' | 'pay';

/**
 * One place that decides what every failure means for the payer. `error` is an axios error (or a
 * `ThreeDSChainError` from runPaymentLink3ds). The single most important rule: a /pay call whose
 * outcome is unknown (no response, a timeout, a 5xx the contract does not name) must NOT be retried
 * by posting again — the charge may have happened. Those re-read the summary instead.
 */
export function classifyPaymentLinkError(error: unknown, during: PaymentLinkCallKind): PaymentLinkOutcome {
  const err = error as { response?: { status?: number; data?: { error_code?: string; data?: { attempts_remaining?: number } } }; code?: string };
  if (error instanceof ThreeDSChainError) return { kind: 'retry', key: RETRY_KEYS[error.code] ?? 'err_THREEDS_FAILED' };

  const status = err?.response?.status;
  const code = err?.response?.data?.error_code;

  if (!status) {
    if (during === 'pay') return { kind: 'poll' };
    if (during === 'summary') return { kind: 'loadError' };
    return { kind: 'retry', key: 'err_NETWORK' };
  }

  switch (code) {
    case 'PAYMENT_LINK_UNAVAILABLE':
      return { kind: 'unavailable' };
    case 'PAYMENT_LINK_LOCKED':
    case 'PAYMENT_DECLINED_LINK_LOCKED':
      return { kind: 'locked' };
    case 'PAYMENT_DECLINED': {
      const left = Number(err.response?.data?.data?.attempts_remaining);
      return { kind: 'retry', key: 'err_PAYMENT_DECLINED', attemptsRemaining: Number.isFinite(left) ? left : undefined };
    }
    case 'PAYMENT_IN_PROGRESS':
      return { kind: 'poll' };
    case 'PAYMENT_LINK_ALREADY_PAID':
      return { kind: 'refresh' };
    case 'PAYMENT_PROCESSING':
      return { kind: 'processing' };
    case 'PAYMENT_SUPERSEDED':
      return { kind: 'stopped', key: 'err_PAYMENT_SUPERSEDED' };
    case 'business_closed':
    case 'service_closed':
      return { kind: 'stopped', key: 'err_business_closed' };
  }
  if (code && RETRY_KEYS[code]) return { kind: 'retry', key: RETRY_KEYS[code] };

  if (status === 404) return { kind: 'unavailable' };
  if (status === 429) return { kind: 'locked' };
  if (during === 'summary') return { kind: 'loadError' };
  // An unnamed 5xx from /pay: outcome unknown → re-read, never re-post.
  if (during === 'pay' && status >= 500 && status !== 502 && status !== 503) return { kind: 'poll' };
  return { kind: 'retry', key: 'err_GENERIC' };
}

/** The 2xx answers of POST /pay: 200 paid summary, or 202 PAYMENT_PROCESSING. */
export function outcomeForPayResponse(
  status: number,
  envelope: { error_code?: string; data?: unknown },
): { kind: 'paid'; summary: PaymentLinkSummary } | PaymentLinkOutcome {
  if (status === 202 || envelope.error_code === 'PAYMENT_PROCESSING') return { kind: 'processing' };
  const summary = envelope.data as PaymentLinkSummary | undefined;
  if (summary && summary.status === 'paid') return { kind: 'paid', summary };
  return { kind: 'poll' }; // unexpected 2xx body: confirm through the summary, never re-post
}

// ─── Countdown ───────────────────────────────────────────────────────────────

export function msRemaining(deadline: number | undefined, now: number): number {
  if (deadline === undefined) return 0;
  return Math.max(0, deadline - now);
}

/** "14:05" (or "1:02:03" past an hour). Rounds UP so "0:00" only shows when time is truly out. */
export function formatCountdown(ms: number): string {
  const total = Math.max(0, Math.ceil(ms / 1000));
  const h = Math.floor(total / 3600);
  const m = Math.floor((total % 3600) / 60);
  const s = total % 60;
  const pad = (n: number) => String(n).padStart(2, '0');
  return h > 0 ? `${h}:${pad(m)}:${pad(s)}` : `${pad(m)}:${pad(s)}`;
}

// ─── Business-local date/time and money ──────────────────────────────────────

/** Every SAM tenant today is America/Santo_Domingo (UTC-4, no DST) — business_calendar.LOCAL_OFFSET_HOURS. */
export const BUSINESS_TIME_ZONE = 'America/Santo_Domingo';
const BUSINESS_OFFSET = '-04:00';

const localeFor = (lang: Lang) => (lang === 'es' ? 'es-DO' : 'en-US');

/** A naive value is already business-local wall-clock (booking_access.py); one with an offset is converted. */
export function parseBusinessDateTime(value: string | null | undefined): Date | null {
  if (!value) return null;
  let iso = String(value).trim().replace(' ', 'T');
  if (/^\d{4}-\d{2}-\d{2}$/.test(iso)) iso = `${iso}T00:00:00`;
  const hasZone = /(Z|[+-]\d{2}:?\d{2})$/.test(iso);
  const date = new Date(hasZone ? iso : `${iso}${BUSINESS_OFFSET}`);
  return Number.isNaN(date.getTime()) ? null : date;
}

export function formatBusinessDate(value: string | null | undefined, lang: Lang): string {
  const d = parseBusinessDateTime(value);
  if (!d) return '';
  return new Intl.DateTimeFormat(localeFor(lang), {
    timeZone: BUSINESS_TIME_ZONE, weekday: 'long', day: 'numeric', month: 'long', year: 'numeric',
  }).format(d);
}

export function formatBusinessTime(value: string | null | undefined, lang: Lang): string {
  const d = parseBusinessDateTime(value);
  if (!d) return '';
  return new Intl.DateTimeFormat(localeFor(lang), {
    timeZone: BUSINESS_TIME_ZONE, hour: 'numeric', minute: '2-digit',
  }).format(d);
}

/** "10:00 AM – 2:00 PM", or just the start when there is no distinct end. */
export function formatBusinessTimeRange(start: string | null | undefined, end: string | null | undefined, lang: Lang): string {
  const a = formatBusinessTime(start, lang);
  const b = formatBusinessTime(end, lang);
  const sa = parseBusinessDateTime(start)?.getTime();
  const sb = parseBusinessDateTime(end)?.getTime();
  return b && sa !== sb ? `${a} – ${b}` : a;
}

export function formatBusinessDateTime(value: string | null | undefined, lang: Lang): string {
  const d = parseBusinessDateTime(value);
  if (!d) return '';
  return new Intl.DateTimeFormat(localeFor(lang), {
    timeZone: BUSINESS_TIME_ZONE, day: 'numeric', month: 'short', year: 'numeric', hour: 'numeric', minute: '2-digit',
  }).format(d);
}

/**
 * Display only — the server's own figure, never used to build a request. Same format as the
 * service editor's live preview (utils/money.formatMoneyAmount): "RD$ 1,000" for DOP, "US$ 150"
 * for USD, two decimals only when there are cents. When `currency` is missing the amount gets a
 * bare "$": never invent one.
 */
export function formatMoney(
  amount: number | string | null | undefined,
  currency: string | null | undefined,
  lang: Lang,
): string {
  return formatMoneyAmount(amount, currency, localeFor(lang));
}

export function pickText(text: { en?: string | null; es?: string | null } | undefined, lang: Lang): string {
  if (!text) return '';
  const primary = text[lang];
  if (primary && String(primary).trim()) return String(primary);
  return String(text[lang === 'en' ? 'es' : 'en'] ?? '');
}

// ─── Card form → request bodies (never a price) ──────────────────────────────

export interface CardFormValue {
  cardNumber: string;
  cardHolder: string;
  expiryDate: string; // MM/YY
  cvv: string;
}

export const EMPTY_CARD: CardFormValue = { cardNumber: '', cardHolder: '', expiryDate: '', cvv: '' };

export interface CardBrand {
  label: string;
  accepted: boolean;
}

/** Brand from the number's prefix, shown inside the card field. American Express (34/37) is refused
 * server-side (SAM-814 card_brand.py), so it is not accepted here either. Unknown → null. */
export function cardBrandOf(cardNumber: string): CardBrand | null {
  const d = cardNumber.replace(/\D/g, '');
  if (/^3[47]/.test(d)) return { label: 'American Express', accepted: false };
  if (/^4/.test(d)) return { label: 'Visa', accepted: true };
  if (/^5[1-5]/.test(d)) return { label: 'Mastercard', accepted: true };
  const four = Number(d.slice(0, 4));
  if (d.length >= 4 && four >= 2221 && four <= 2720) return { label: 'Mastercard', accepted: true };
  if (/^(6011|65|64[4-9])/.test(d)) return { label: 'Discover', accepted: true };
  if (/^35/.test(d)) return { label: 'JCB', accepted: true };
  if (/^(36|38|30[0-5])/.test(d)) return { label: 'Diners Club', accepted: true };
  return null;
}

/** Same rules the shared CreditCardInput shows inline; gates the Pay button. */
export function isCardFormComplete(v: CardFormValue, now: Date = new Date()): boolean {
  const digits = v.cardNumber.replace(/\s/g, '');
  if (!/^\d{16}$/.test(digits)) return false;
  if (cardBrandOf(digits)?.accepted === false) return false;
  if (v.cardHolder.trim().length < 3 || !/^[\p{L}\s'.-]+$/u.test(v.cardHolder)) return false;
  const m = v.expiryDate.match(/^(\d{2})\/(\d{2})$/);
  if (!m) return false;
  const month = Number(m[1]);
  const year = Number(m[2]);
  if (month < 1 || month > 12) return false;
  const cy = now.getFullYear() % 100;
  const cm = now.getMonth() + 1;
  if (year < cy || (year === cy && month < cm)) return false;
  return /^\d{3,4}$/.test(v.cvv);
}

export function toPaymentLinkCard(v: CardFormValue): PaymentLinkCard {
  const [month = '', year = ''] = v.expiryDate.split('/');
  return {
    number: v.cardNumber.replace(/\s/g, ''),
    expiration_month: month,
    expiration_year: year.length === 2 ? `20${year}` : year,
    security_code: v.cvv,
  };
}

/** Exactly the fields POST /pay reads. No amount, currency or line item — the server charges the link's stored total. */
export function buildPayBody(card: PaymentLinkCard, cardholderName: string, authData?: PaymentLinkAuthData): PaymentLinkPayBody {
  const body: PaymentLinkPayBody = { card, cardholder_name: cardholderName.trim() };
  if (authData) body.auth_data = authData;
  return body;
}

// ─── 3DS chain over POST /{token}/3ds ────────────────────────────────────────

export class ThreeDSChainError extends Error {
  constructor(public readonly code: 'THREEDS_FAILED' | 'THREEDS_TIMEOUT') {
    super(code);
    this.name = 'ThreeDSChainError';
  }
}

export interface ThreeDSDeps {
  post3ds: <T>(body: PaymentLink3dsBody) => Promise<T>;
  /** PortalApiService.collectDeviceData — the same hidden-iframe Cardinal collection every flow uses. */
  collectDeviceData: (url: string, jwt: string) => Promise<unknown>;
  /** Opens the shared ThreeDSModal step-up and resolves with Cardinal's TransactionId. */
  challenge: (p: { stepUpUrl: string; accessToken: string; authTransactionId: string }) => Promise<string>;
  /** window.location.origin — setup's return_url, as BookingPaymentService.setupPayerAuth sends. */
  setupReturnUrl: string;
  /** resolveThreeDSReturnUrl() — where Cardinal POSTs after a challenge (ThreeDSModal detection). */
  stepUpReturnUrl: string;
}

/**
 * setup → device data collection → enroll → (challenge → validate) → auth_data. Mirrors
 * AccessPage.processServiceBookingAuth step for step, but against the token-scoped route, so the
 * body never carries an amount: run_3ds_step takes amount/currency/merchant from the link.
 */
export async function runPaymentLink3ds(card: PaymentLinkCard, cardholderName: string, deps: ThreeDSDeps): Promise<PaymentLinkAuthData> {
  const common = { card, cardholder_name: cardholderName.trim() };
  const setup = await deps.post3ds<PaymentLinkSetupData>({ step: 'setup', ...common, return_url: deps.setupReturnUrl });
  if (!setup?.device_data_collection_url || !setup?.access_token || !setup?.reference_id) {
    throw new ThreeDSChainError('THREEDS_FAILED');
  }
  await deps.collectDeviceData(setup.device_data_collection_url, setup.access_token);

  const enroll = await deps.post3ds<PaymentLinkEnrollData>({
    step: 'enroll',
    ...common,
    reference_id: setup.reference_id,
    merchant_reference: setup.merchant_reference,
    return_url: deps.stepUpReturnUrl,
  });
  if (enroll?.status === 'AUTHENTICATION_SUCCESSFUL' && enroll.auth_data) return enroll.auth_data;
  if (enroll?.status === 'PENDING_AUTHENTICATION' && enroll.step_up_url && enroll.access_token) {
    const transactionId = await deps.challenge({
      stepUpUrl: enroll.step_up_url,
      accessToken: enroll.access_token,
      authTransactionId: enroll.authentication_transaction_id || '',
    });
    const validated = await deps.post3ds<PaymentLinkValidateData>({
      step: 'validate',
      ...common,
      authentication_transaction_id: transactionId || enroll.authentication_transaction_id || '',
      merchant_reference: setup.merchant_reference,
    });
    if (validated?.status === 'AUTHENTICATION_SUCCESSFUL' && validated.auth_data) return validated.auth_data;
  }
  throw new ThreeDSChainError('THREEDS_FAILED');
}

// ─── Entry pass (paid state) ─────────────────────────────────────────────────

export type PassView =
  | { kind: 'none' }
  | {
      kind: 'qr';
      qrValue: string;
      passUrl?: string;
      window: string;
      guests?: number;
      children?: number;
      idVerified: boolean;
      doorPin?: { pin?: string; pending: boolean };
    }
  | { kind: 'verify'; passUrl?: string; window: string }
  | { kind: 'used' | 'cancelled' | 'unavailable' };

const isEndOfDay = (d: Date) => {
  const parts = new Intl.DateTimeFormat('en-US', { timeZone: BUSINESS_TIME_ZONE, hour: 'numeric', minute: 'numeric', hourCycle: 'h23' })
    .formatToParts(d);
  const h = Number(parts.find((p) => p.type === 'hour')?.value);
  const m = Number(parts.find((p) => p.type === 'minute')?.value);
  return h === 23 && m === 59;
};
const isStartOfDay = (d: Date) => {
  const parts = new Intl.DateTimeFormat('en-US', { timeZone: BUSINESS_TIME_ZONE, hour: 'numeric', minute: 'numeric', hourCycle: 'h23' })
    .formatToParts(d);
  return Number(parts.find((p) => p.type === 'hour')?.value) === 0 && Number(parts.find((p) => p.type === 'minute')?.value) === 0;
};
const businessDayKey = (d: Date) =>
  new Intl.DateTimeFormat('en-CA', { timeZone: BUSINESS_TIME_ZONE, year: 'numeric', month: '2-digit', day: '2-digit' }).format(d);

/**
 * The suggested file name of the printed receipt (the browser takes it from the document title): "Receipt <business> <date>",
 * the date being the day of the payment in business time (yyyy-mm-dd, today when the payment has no date yet).
 */
export function receiptDocumentTitle(label: string, businessName: string | null | undefined, paidAt: string | null | undefined): string {
  const day = businessDayKey(parseBusinessDateTime(paidAt) ?? new Date());
  const name = (businessName || '').replace(/[\\/:*?"<>|]+/g, ' ');
  return [label, name, day].join(' ').replace(/\s+/g, ' ').trim();
}

/**
 * When the pass opens the gate, in business-local time. A full local day (booking passes are valid
 * the whole day of the reservation — booking_access "Validity window") reads as just that date;
 * anything else as "from – until".
 */
export function formatPassWindow(from: string | null | undefined, until: string | null | undefined, lang: Lang): string {
  const a = parseBusinessDateTime(from);
  const b = parseBusinessDateTime(until);
  if (!a) return '';
  if (!b) return formatBusinessDateTime(from, lang);
  if (businessDayKey(a) === businessDayKey(b) && isStartOfDay(a) && isEndOfDay(b)) return formatBusinessDate(from, lang);
  if (businessDayKey(a) === businessDayKey(b)) {
    return `${formatBusinessDate(from, lang)}, ${formatBusinessTimeRange(from, until, lang)}`;
  }
  return `${formatBusinessDateTime(from, lang)} – ${formatBusinessDateTime(until, lang)}`;
}

/** Only an http(s) link is ever rendered as a link (the page never follows anything else). */
function safeUrl(value: string | null | undefined): string | undefined {
  if (!value) return undefined;
  try {
    const url = new URL(value);
    return url.protocol === 'https:' || url.protocol === 'http:' ? url.toString() : undefined;
  } catch {
    return undefined;
  }
}

/** What the paid screen shows for the pass. No `pass` (older backend) shows nothing at all. */
export function passViewFor(pass: PaymentLinkPass | null | undefined, lang: Lang): PassView {
  if (!pass || typeof pass !== 'object') return { kind: 'none' };
  const window = formatPassWindow(pass.valid_from, pass.valid_until, lang);
  const passUrl = safeUrl(pass.pass_url);
  switch (pass.state) {
    case 'ready': {
      const qrValue = typeof pass.qr_value === 'string' ? pass.qr_value.trim() : '';
      if (!qrValue) return { kind: 'unavailable' };
      const total = Number(pass.guests?.total);
      const children = Number(pass.guests?.children);
      const pinStatus = pass.door_pin?.status || undefined;
      const pin = pass.door_pin?.pin ? String(pass.door_pin.pin) : undefined;
      const doorPin =
        pinStatus === 'ok' && pin ? { pin, pending: false }
          : pinStatus && pinStatus !== 'skipped' && pinStatus !== 'ok' ? { pending: true }
            : undefined;
      return {
        kind: 'qr',
        qrValue,
        passUrl,
        window,
        guests: Number.isFinite(total) && total > 0 ? total : undefined,
        children: Number.isFinite(children) && children > 0 ? children : undefined,
        idVerified: pass.id_verified === true,
        doorPin,
      };
    }
    case 'verification_required':
      return { kind: 'verify', passUrl, window };
    case 'used':
      return { kind: 'used' };
    case 'cancelled':
      return { kind: 'cancelled' };
    default:
      return { kind: 'unavailable' };
  }
}
